import React, { useState, useEffect } from "react";
import { PROTECTED_EMAILS, decodeEmail } from "../utils/emailProtection";
import { useLiveChat } from "../utils/tawkto";
import { getTeamAvailability } from "../utils/teamAvailability";
import "../css/contact-scoped.css";
import EnhancedSEO from "../Components/EnhancedSEO";
import { 
  generateMetaTags, 
  pageConfigs, 
  organizationSchema
} from "../utils/seoUtils";

// Icons
import { 
  FaEnvelope, 
  FaWhatsapp, 
  FaClock,
  FaHeadset,
  FaRocket,
  FaShieldAlt,
  FaBolt,
  FaUsers,
  FaTrophy,
  FaComments,
  FaQuestionCircle,
  FaCreditCard,
  FaTools,
  FaPhone,
  FaCircle
} from "react-icons/fa";

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    category: 'general'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [teamAvailability, setTeamAvailability] = useState(getTeamAvailability());
  const { handleLiveChatClick } = useLiveChat();

  // Update team availability every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setTeamAvailability(getTeamAvailability());
    }, 60000); // Update every 60 seconds

    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Construct email body
    const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Category: ${formData.category}
Subject: ${formData.subject}

Message:
${formData.message}
    `;
    
    // Open email client
    window.location.href = `mailto:${decodeEmail(PROTECTED_EMAILS.SUPPORT)}?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(emailBody)}`;
    
    setTimeout(() => {
      setIsSubmitting(false);
    }, 2000);
  };

  // Enhanced SEO configuration
  const contactPageConfig = generateMetaTags(pageConfigs.contact);
  
  // Structured data for contact page
  const contactStructuredData = [
    {
      ...organizationSchema,
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+91-011-6931-0286",
          "contactType": "customer service",
          "availableLanguage": ["English", "Hindi"],
          "areaServed": "IN"
        },
        {
          "@type": "ContactPoint",
          "email": "support@clubhosty.com",
          "contactType": "technical support",
          "availableLanguage": ["English", "Hindi"],
          "areaServed": "IN"
        }
      ]
    }
  ];

  return (
    <div className="contact-page-wrapper">
      <EnhancedSEO 
        pageConfig={contactPageConfig}
        structuredData={contactStructuredData}
      />

      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="hero-content">
                <div className="hero-badge">
                  <FaHeadset className="badge-icon" />
                  <span>24/7 Support Available</span>
                </div>
                <h1 className="hero-title">
                  Get in Touch with <span className="text-gradient">ClubHosty</span>
                </h1>
                <p className="hero-description">
                  Our expert support team is here to help you succeed online. Whether you need 
                  technical assistance, have questions about our services, or need help choosing 
                  the right hosting plan - we're here 24/7/365.
                </p>
                <div className="hero-stats">
                  <div className="stat-item">
                    <div className="stat-number">&lt;15min</div>
                    <div className="stat-label">Response Time</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">24/7</div>
                    <div className="stat-label">Support Available</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">98%</div>
                    <div className="stat-label">Customer Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image">
                <img 
                  src="https://res.cloudinary.com/clubhosty/image/upload/v1752978397/generated-image_bfaxwk.png" 
                  alt="24/7 Customer Support" 
                  className="img-fluid support-hero-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <main id="main">
        {/* Contact Methods Section */}
        <section className="contact-methods quick-contact">
          <div className="container">
            <div className="section-header">
              <h2>Contact Us Directly</h2>
              <p>Choose your preferred way to reach us</p>
            </div>
            <div className="row justify-content-center">
              <div className="col-lg-4 col-md-6 mb-4">
                <div className="contact-method-card instant">
                  <div className="method-icon">
                    <FaComments />
                  </div>
                  <div className="status-indicator">
                    <FaCircle className={`status-dot ${teamAvailability.liveChat.status}`} />
                    <span className="status-text">{teamAvailability.liveChat.label}</span>
                  </div>
                  <h4>Live Chat</h4>
                  <p>Instant support via our live chat system</p>
                  <button 
                    className="btn-method-action"
                    onClick={handleLiveChatClick}
                  >
                    Start Chat
                  </button>
                  <div className="badge-instant">Instant</div>
                </div>
              </div>

              <div className="col-lg-4 col-md-6 mb-4">
                <div className="contact-method-card">
                  <div className="method-icon">
                    <FaPhone />
                  </div>
                  <div className="status-indicator">
                    <FaCircle className={`status-dot ${teamAvailability.phone.status}`} />
                    <span className="status-text">{teamAvailability.phone.label}</span>
                  </div>
                  <h4>Phone Support</h4>
                  <p>Talk directly to our support team</p>
                  <a href="tel:+9101169310286" className="btn-method-action">
                    Call Now
                  </a>
                  <div className="method-hours">{teamAvailability.phone.hours}</div>
                </div>
              </div>

              <div className="col-lg-4 col-md-6 mb-4">
                <div className="contact-method-card">
                  <div className="method-icon">
                    <FaWhatsapp />
                  </div>
                  <div className="status-indicator">
                    <FaCircle className={`status-dot ${teamAvailability.whatsapp.status}`} />
                    <span className="status-text">{teamAvailability.whatsapp.label}</span>
                  </div>
                  <h4>WhatsApp</h4>
                  <p>Quick support via WhatsApp messaging</p>
                  <a href="https://wa.me/+911169310286" className="btn-method-action">
                    Message Us
                  </a>
                  <div className="method-hours">{teamAvailability.whatsapp.hours}</div>
                </div>
              </div>

              <div className="col-lg-4 col-md-6 mb-4">
                <div className="contact-method-card">
                  <div className="method-icon">
                    <FaQuestionCircle />
                  </div>
                  <div className="status-indicator">
                    <FaCircle className={`status-dot ${teamAvailability.ticket.status}`} />
                    <span className="status-text">{teamAvailability.ticket.label}</span>
                  </div>
                  <h4>Ticket Support</h4>
                  <p>Submit a detailed support ticket</p>
                  <a href="https://go2ch.in/ticket" className="btn-method-action">
                    Create Ticket
                  </a>
                  <div className="method-hours">{teamAvailability.ticket.hours}</div>
                </div>
              </div>

              <div className="col-lg-4 col-md-6 mb-4">
                <div className="contact-method-card">
                  <div className="method-icon">
                    <FaEnvelope />
                  </div>
                  <div className="status-indicator">
                    <FaCircle className={`status-dot ${teamAvailability.email.status}`} />
                    <span className="status-text">{teamAvailability.email.label}</span>
                  </div>
                  <h4>Email Support</h4>
                  <p>Detailed support via email</p>
                  <button 
                    className="btn-method-action"
                    onClick={() => window.location.href = `mailto:${decodeEmail(PROTECTED_EMAILS.SUPPORT)}`}
                  >
                    Send Email
                  </button>
                  <div className="method-hours">{teamAvailability.email.hours}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="contact-form-section">
          <div className="container">
            <div className="row">
              <div className="col-lg-8">
                <div className="form-container">
                  <div className="form-header">
                    <h2>Send Us a Message</h2>
                    <p>Fill out the form below and we'll get back to you as soon as possible.</p>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="contact-form-modern">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="name">Your Name *</label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="form-control"
                            required
                            placeholder="Enter your full name"
                          />
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="email">Your Email *</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="form-control"
                            required
                            placeholder="Enter your email address"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="category">Support Category</label>
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        <option value="general">General Inquiry</option>
                        <option value="technical">Technical Support</option>
                        <option value="billing">Billing & Account</option>
                        <option value="sales">Sales & Pre-Sales</option>
                        <option value="migration">Website Migration</option>
                        <option value="domain">Domain Services</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="subject">Subject *</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                        placeholder="Brief description of your inquiry"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="message">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="form-control"
                        rows="6"
                        required
                        placeholder="Please provide detailed information about your inquiry..."
                      ></textarea>
                    </div>
                    
                    <div className="form-submit">
                      <button 
                        type="submit" 
                        className="btn-submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                        <FaRocket className="btn-icon" />
                      </button>
                      <p className="form-note">
                        We'll respond within 15 minutes during business hours
                      </p>
                    </div>
                  </form>
                </div>
              </div>
              
              <div className="col-lg-4">
                <div className="contact-sidebar">
                  {/* Support Types */}
                  <div className="support-types">
                    <h3>How We Can Help</h3>
                    
                    <div className="support-item">
                      <div className="support-icon">
                        <FaTools />
                      </div>
                      <div className="support-content">
                        <h5>Technical Support</h5>
                        <p>Server issues, email problems, SSL certificates, and more</p>
                      </div>
                    </div>
                    
                    <div className="support-item">
                      <div className="support-icon">
                        <FaCreditCard />
                      </div>
                      <div className="support-content">
                        <h5>Billing & Account</h5>
                        <p>Payment issues, plan upgrades, refunds, and account management</p>
                      </div>
                    </div>
                    
                    <div className="support-item">
                      <div className="support-icon">
                        <FaRocket />
                      </div>
                      <div className="support-content">
                        <h5>Migration Help</h5>
                        <p>Free website migration from your current hosting provider</p>
                      </div>
                    </div>
                    
                    <div className="support-item">
                      <div className="support-icon">
                        <FaQuestionCircle />
                      </div>
                      <div className="support-content">
                        <h5>Pre-Sales</h5>
                        <p>Help choosing the right hosting plan for your needs</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contact Info */}
                  <div className="contact-info">
                    <h3>Contact Information</h3>
                    
                    <div className="info-item">
                      <FaPhone className="info-icon" />
                      <div className="info-content">
                        <strong>Phone</strong>
                        <p><a href="tel:+910116931028" style={{ color: "inherit", textDecoration: "none" }}>+91 011 6931 0286</a><br />9:00 AM - 6:00 PM IST</p>
                      </div>
                    </div>
                    
                    <div className="info-item">
                      <FaClock className="info-icon" />
                      <div className="info-content">
                        <strong>Support Hours</strong>
                        <p>Email support: 24/7<br />WhatsApp: 9:00 AM - 6:00 PM IST</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Support Features */}
        <section className="support-features">
          <div className="container">
            <div className="section-header">
              <h2>Why Choose ClubHosty Support?</h2>
              <p>We're committed to providing exceptional support that helps you succeed online</p>
            </div>
            
            <div className="row">
              <div className="col-lg-3 col-md-6 mb-4">
                <div className="feature-card">
                  <div className="feature-icon">
                    <FaBolt />
                  </div>
                  <h4>Fast Response</h4>
                  <p>83% of issues resolved in under 15 minutes during business hours</p>
                </div>
              </div>
              
              <div className="col-lg-3 col-md-6 mb-4">
                <div className="feature-card">
                  <div className="feature-icon">
                    <FaUsers />
                  </div>
                  <h4>Expert Team</h4>
                  <p>Experienced professionals with deep knowledge of web hosting</p>
                </div>
              </div>
              
              <div className="col-lg-3 col-md-6 mb-4">
                <div className="feature-card">
                  <div className="feature-icon">
                    <FaShieldAlt />
                  </div>
                  <h4>3-Tier Escalation</h4>
                  <p>Complex issues are escalated to specialized technical experts</p>
                </div>
              </div>
              
              <div className="col-lg-3 col-md-6 mb-4">
                <div className="feature-card">
                  <div className="feature-icon">
                    <FaTrophy />
                  </div>
                  <h4>We Deliver</h4>
                  <p>We work until we deliver on our promises, no matter how long it takes</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="container">
            <div className="section-header">
              <h2>Frequently Asked Questions</h2>
              <p>Quick answers to common questions</p>
            </div>
            
            <div className="row">
              <div className="col-lg-8 mx-auto">
                <div className="faq-container">
                  <div className="faq-item">
                    <div className="faq-question">
                      <FaQuestionCircle className="faq-icon" />
                      <strong>How quickly do you respond to support tickets?</strong>
                    </div>
                    <div className="faq-answer">
                      We respond to 83% of support requests within 15 minutes during business hours (9 AM - 6 PM IST). Email support is available 24/7 with responses typically within 2-4 hours.
                    </div>
                  </div>
                  
                  <div className="faq-item">
                    <div className="faq-question">
                      <FaQuestionCircle className="faq-icon" />
                      <strong>Do you offer free website migration?</strong>
                    </div>
                    <div className="faq-answer">
                      Yes! We offer free website migration for all new customers. Our technical team will handle the entire migration process to ensure zero downtime.
                    </div>
                  </div>
                  
                  <div className="faq-item">
                    <div className="faq-question">
                      <FaQuestionCircle className="faq-icon" />
                      <strong>What support channels do you offer?</strong>
                    </div>
                    <div className="faq-answer">
                      We offer multiple support channels including live chat, WhatsApp, phone support, and email tickets. Choose the method that works best for you.
                    </div>
                  </div>
                  
                  <div className="faq-item">
                    <div className="faq-question">
                      <FaQuestionCircle className="faq-icon" />
                      <strong>Is technical support included in all plans?</strong>
                    </div>
                    <div className="faq-answer">
                      Yes, technical support is included free of charge with all hosting plans. We don't charge extra for helping you with hosting-related issues.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Contact;
